import React, { useState, useEffect } from 'react';
import { createSession, getSessions } from '../storage';
import { loadDB, uid } from '../db';
import Login from './Login';
import SessionPicker from './SessionPicker';
import { toast } from '../ui.jsx';

function LoginFlow({ onLogin, skipAuth, authSession: initialAuth }) {
  const [step, setStep] = useState(skipAuth ? 'session' : 'auth');
  const [authSession, setAuthSession] = useState(initialAuth || null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [authDB, setAuthDB] = useState(loadDB());

  const fetchSessions = () => {
    setSessionsLoading(true);
    getSessions()
      .then(list => setSessions(list))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  };

  // Fetch on step change to 'session'
  useEffect(() => {
    if (step === 'session') fetchSessions();
  }, [step]); // eslint-disable-line

  // Also fetch immediately on mount if skipAuth (already logged in, just picking session)
  useEffect(() => {
    if (skipAuth) fetchSessions();
  }, []); // eslint-disable-line

  const handleAuth = (sess, sessId) => {
    // Parent login: go directly to their session
    if (sess.role === 'parent') {
      onLogin(sess, sess.user.sessionId);
      return;
    }
    // Teacher login: sessId already resolved, skip session picker
    if (sess.role === 'teacher' && sessId) {
      onLogin(sess, sessId);
      return;
    }
    setAuthSession(sess);
    setStep('session');
  };

  const handlePickSession = (sessId) => {
    onLogin(authSession, sessId);
  };

  const handleCreateSession = async (name, year, carryOver) => {
    const id = 'sess_' + uid();
    const lastSess = sessions.length > 0 ? sessions[sessions.length - 1] : null;
    const carryFrom = lastSess ? { sessionId: lastSess.sid || lastSess.id, ...carryOver } : undefined;
    try {
      await createSession(id, name, year, carryFrom);
      const updated = await getSessions();
      setSessions(updated);
      onLogin(authSession, id);
    } catch (err) {
      toast('Failed to create session: ' + err.message, 'err');
    }
  };

  if (step === 'auth') return <Login db={authDB} onLogin={handleAuth} />;
  if (sessionsLoading && sessions.length === 0) {
    return <div className="flex items-center justify-center h-screen bg-surface text-sm text-on-surface-variant">Loading sessions…</div>;
  }
  return <SessionPicker sessions={sessions} authSession={authSession} onPick={handlePickSession} onCreate={handleCreateSession} onSignOut={() => setStep('auth')} onRefresh={fetchSessions} />;
}

export default LoginFlow;
